import type { PageMeta } from '@rd/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from './Button.js';

/**
 * Driven by the server's `meta`, never by a client-side count.
 *
 * `total` is the only thing that knows how many pages there are; deriving it
 * from `data.length` would show "page 1 of 1" on every full page.
 *
 * The controls are the 28px `sm` button: pagination sits directly under a table
 * frame and is an action *on* that object rather than an object of its own, so
 * full-height buttons here out-weigh the data they page through. The counts are
 * `tabular-nums` for the same reason the numeric columns are — the range
 * changes on every page and proportional digits make it jump.
 */
export function Pagination({
  meta,
  onPageChange,
  label,
}: {
  meta: PageMeta;
  onPageChange(page: number): void;
  label: string;
}): ReactElement | null {
  const lastPage = Math.max(1, Math.ceil(meta.total / Math.max(1, meta.pageSize)));
  if (meta.total === 0) return null;

  const first = (meta.page - 1) * meta.pageSize + 1;
  const last = Math.min(meta.total, meta.page * meta.pageSize);

  return (
    <nav
      aria-label={label}
      className="flex flex-wrap items-center justify-between gap-[var(--space-4)] py-[var(--space-3)]"
    >
      <p className="text-[length:var(--text-sm)] tabular-nums text-muted" aria-live="polite">
        {first}–{last} of {meta.total}
      </p>
      <div className="flex items-center gap-[var(--space-2)]">
        {/* The glyphs are `aria-hidden` inside the button, so the accessible
            name stays exactly "Previous" / "Next" — direction is carried by the
            word for a screen reader and by the arrow for everyone else. */}
        <Button
          variant="secondary"
          size="sm"
          icon={ChevronLeft}
          disabled={meta.page <= 1}
          onClick={() => onPageChange(meta.page - 1)}
        >
          Previous
        </Button>
        <span className="text-[length:var(--text-sm)] tabular-nums text-muted">
          Page {meta.page} of {lastPage}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={meta.page >= lastPage}
          onClick={() => onPageChange(meta.page + 1)}
        >
          Next
          <ChevronRight aria-hidden="true" size={14} strokeWidth={2} className="shrink-0" />
        </Button>
      </div>
    </nav>
  );
}
